/**
 * DailyOps — the restaurant's end-of-day operations board.
 *
 * At close, staff jot down what's needed for tomorrow across the daily lists —
 * 🧑‍🍳 Chef Groceries, 🛒 Groceries, 🥡 To-Go, 🔪 Prep, 🍳 Cooking, 🧽 Cleaning and ⚠️ Issues,
 * plus any list staff make in the app — and the standing par-check tabs
 * (🛒 Shopping, 📦 Backup, 🔪 Cutting). The board is ONE open list that stays until
 * someone moves it to History; the first add of a new list cycle (from closing time)
 * asks whether to start a new list or keep adding. The app learns from the saved
 * history (like a kitchen's par sheet) and suggests what's usually needed, so rebuilding
 * the list each night takes seconds. Everything is live-shared across devices via
 * Firestore, and the History tab keeps a searchable record per day.
 *
 * Folder map (see README.md): config/ static data · logic/ pure rules (tested) ·
 * data/ all Firestore I/O · hooks/ React hooks over data + logic · i18n/ English/Telugu ·
 * components/ the screens, one folder per tab, shared/ for what they share.
 */
import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { STAFF_ROLES } from '../../utils/roles';
import { localDateISO, quotaResetTime } from './logic/dates';
import { buildCatalogs } from './logic/catalogs';
import { isFlagged } from './logic/items';
import { isOpenItem } from './logic/openList';
import { buildListsState } from './logic/lists';
import { HISTORY_WINDOW_DAYS } from './config/constants';
import { CHECKLISTS } from './config/checklists';
import { RETIRED_LIST_IDS } from './config/lists';
import { loadErrorKey } from './data/errors';
import { ListsCtx, MobileCtx, OpenListCtx } from './context';
import { LangProvider, useLang, LANGS } from './i18n';
import { useTranslationEngine } from './i18n/translationEngine';
import { useRecentItems } from './hooks/useRecentItems';
import { useIsMobile } from './hooks/useIsMobile';
import { useCycleTick } from './hooks/useCycleTick';
import { useListDocs } from './hooks/useListDocs';
import { useOpenListGate } from './hooks/useOpenListGate';
import { useStableCatalogs } from './hooks/useStable';
import { authorName } from './hooks/useAuthor';
import { ToastHost } from './components/shared/ToastHost';
import { HelpModal } from './components/help/HelpModal';
import { ListDialog } from './components/today/ListDialog';
import { TodayTab } from './components/today/TodayTab';
import { ClosingTab } from './components/closing/ClosingTab';
import { ChecklistTab } from './components/checklists/ChecklistTab';
import { ReviewTab } from './components/review/ReviewTab';
import { HistoryTab } from './components/history/HistoryTab';
import './DailyOps.css';

const TABS = ['today', 'closing', 'shopping', 'backup', 'cutting', 'review', 'history'];

// LangProvider carries the shared Firestore auto-translate engine (new item names get
// Telugu without hand-curation); ToastHost owns the undo toast. Both sit inside
// `.do-root`, whose CSS variables the toast and dialogs use.
export default function DailyOps() {
  const engine = useTranslationEngine();
  return (
    <LangProvider engine={engine}>
      <div className="do-root">
        <ToastHost>
          <DailyOpsBoard />
        </ToastHost>
      </div>
    </LangProvider>
  );
}

function DailyOpsBoard() {
  const { t, lang, setLang } = useLang();
  const { user, role } = useAuth();
  const [tab, setTab] = useState('today');
  const [showHelp, setShowHelp] = useState(false);
  const { items: allLoaded, truncated, loading, error } = useRecentItems(HISTORY_WINDOW_DAYS);
  // The lists — built-ins + the ones staff made / restyled in the app — and item routing.
  const listDocs = useListDocs();
  const lists = useMemo(
    () => buildListsState(listDocs.docs, { ready: listDocs.ready, error: listDocs.error }),
    [listDocs.docs, listDocs.ready, listDocs.error],
  );
  // Ignore items saved under retired lists (grocery_order / cutting now live in the
  // Shopping & Cutting tabs) so counts, history and carry-over stay consistent.
  const items = useMemo(() => allLoaded.filter(i => !RETIRED_LIST_IDS.includes(i.category)), [allLoaded]);
  useCycleTick();   // re-render at 8 PM / midnight so an idle iPad's board updates itself
  const today = localDateISO();
  const author = authorName(user);
  const isMobile = useIsMobile();

  // The board = the OPEN list: everything not yet moved to History, whatever day it was
  // noted (no midnight wipe), in a list that's on the board. Archived items stay in the
  // collection for History.
  const boardItems = useMemo(
    () => items.filter(i => isOpenItem(i) && lists.byId.has(i.category) && !lists.byId.get(i.category).deleted),
    [items, lists],
  );
  const gate = useOpenListGate(boardItems, author);

  // Suggestions depend only on which names were noted in which list on which day (and,
  // for names removed from a list, whether a note is still being saved) — so they're
  // rebuilt when those change, not on every tick or status change.
  const catalogKey = useMemo(
    () => items.map(i => `${i.id}|${i.name}|${i.category}|${i.date}${i.addedAt === null ? '|*' : ''}`).join('\n'), [items]);
  const catalogs = useStableCatalogs(useMemo(
    () => buildCatalogs(items, today, { lists: lists.active, route: lists.route, hidden: lists.hidden }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `items` is covered by catalogKey
    [catalogKey, today, lists],
  ));
  const flaggedCount = useMemo(
    () => items.filter(i => lists.catById(i.category).sourceable && isFlagged(i)).length,
    [items, lists],
  );

  // The board's feed is down (it re-opens by itself) — say why, in words staff can act on.
  const errorMsg = error ? loadErrorKey(error, quotaResetTime()) : null;

  if (role && !STAFF_ROLES.includes(role)) {
    return <div className="do-denied">🔒 {t('app.accessRestricted')}</div>;
  }

  return (
    <ListsCtx.Provider value={lists}>
      <MobileCtx.Provider value={isMobile}>
        <OpenListCtx.Provider value={gate.ctx}>
          <div className="do-header">
            <div className="do-header__left">
              <h1 className="do-header__title">🧺 <span className="do-header__title-grad">{t('app.title')}</span></h1>
              <button className="do-help-link" onClick={() => setShowHelp(true)} title={t('app.howToUse')}>❓ {t('app.howToUse')}</button>
              <div className="do-lang" role="group" aria-label={t('lang.label')}>
                {LANGS.map(l => (
                  <button key={l.id} className={`do-lang__btn${lang === l.id ? ' do-lang__btn--on' : ''}`}
                    onClick={() => setLang(l.id)} aria-pressed={lang === l.id} title={l.name}>{l.label}</button>
                ))}
              </div>
            </div>
            <div className="do-tabs">
              {TABS.map(id => (
                <button key={id} className={`do-tab${tab === id ? ' do-tab--on' : ''}`} onClick={() => setTab(id)}>
                  {t(`tab.${id}`)}
                  {id === 'review' && flaggedCount > 0 && <span className="do-tab__badge">{flaggedCount}</span>}
                </button>
              ))}
            </div>
          </div>

          {errorMsg && <div className="do-error" role="alert">⚠️ {t(errorMsg.key, errorMsg.vars)}</div>}

          <div className="do-body">
            {tab === 'today' && <TodayTab items={boardItems} allItems={items} catalogs={catalogs} author={author} onReview={() => setTab('review')} onGoShopping={() => setTab('shopping')} />}
            {tab === 'closing' && <ClosingTab todayItems={boardItems} author={author} />}
            {CHECKLISTS.map(c => tab === c.id && <ChecklistTab key={c.id} config={c} author={author} />)}
            {tab === 'review' && <ReviewTab items={items} author={author} onGoToday={() => setTab('today')} />}
            {tab === 'history' && <HistoryTab items={items} loading={loading} truncated={truncated} />}
          </div>

          {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
          {gate.dialog && <ListDialog {...gate.dialog} />}
        </OpenListCtx.Provider>
      </MobileCtx.Provider>
    </ListsCtx.Provider>
  );
}
